/**
 * VectorLayoutView.jsx — renders a /export/vector placements response.
 *
 * The contract (v1.1 §3.5) ships a Fabric.js renderer. This component is the
 * faithful React/SVG equivalent, written in the same idiom as the existing
 * LayoutView in App.js so the two render paths look identical:
 *
 *   - 1 SVG user unit = 1 mm (no viewBox); pan/zoom via a <g transform>.
 *   - Y-flip on every vertex: MNE Y-up → SVG Y-down via  y_svg = shH - y_mne.
 *   - placed_vertices are DEFINITIVE. We never re-apply rotation_deg or
 *     translation — they are already baked in (contract §3.5).
 *   - L-shaped dies (is_rectangular:false / source_geometry:"polygon") are
 *     drawn as a <polygon> with all N points, NOT a bounding rect — the
 *     concave region stays empty (contract §3.5).
 *   - Gripper / edge margin strips drawn from result.sheet.margins, reusing
 *     the same Y-flip mapping as App.js's LayoutView.
 *
 * It reuses useCanvasControls so pan/zoom behavior matches the rest of the app.
 *
 * Props:
 *   result   — the full /export/vector response (contract §3.3), or null.
 *   palette  — optional override of the dark theme tokens (defaults to P below).
 */

import React, { useMemo, useEffect } from 'react';
import { useCanvasControls } from './useCanvasControls';
import { useTheme } from './theme';
import {
  placementCartons, placementSourceLabel, placementCartonCount,
} from './prepress';
// PR ⑦: press-room chrome (dot grid, rulers, crop marks, colour bar).
import SheetChrome, { DotGrid } from './SheetChrome';

// Theme tokens come from the shared ThemeProvider (see theme.js).

export default function VectorLayoutView({ result }) {
  const { P, MO, TC, fmtLen } = useTheme();
  const { zoom, pan, wrapperRef, fit, beginPanDrag } = useCanvasControls({});

  const shW = result?.sheet?.width ?? 0;
  const shH = result?.sheet?.height ?? 0;
  const margins = result?.sheet?.margins || null;

  // Fit the sheet to the viewport whenever the sheet size changes.
  useEffect(() => {
    if (shW > 0 && shH > 0) fit({ contentW: shW, contentH: shH, padding: 0.04, maxZoom: 4 });
  }, [shW, shH, fit]);

  // Pre-compute every drawn CARTON (Y-flipped) once per result.
  //
  // One placement is not one carton. A prepress tile is a single placed piece
  // holding two interlocked cartons, and `placed_vertices` describes the fused
  // die — so drawing one polygon per placement showed a paired carton as a
  // single block twice its real size, under one label. `components[]` carries
  // each carton's own outline in the SAME frame, already through the placement
  // transform (contract §3.5), so nothing is re-applied here. When it is
  // absent, placementCartons falls back to the fused outline and `fused` says
  // the picture is under-describing the die rather than pretending otherwise.
  const items = useMemo(() => {
    if (!result?.placements) return [];
    const out = [];
    result.placements.forEach((p, i) => {
      const label = placementSourceLabel(p, result.sources);
      const cartons = placementCartons(p);
      const worth = placementCartonCount(p);
      cartons.forEach((c, k) => {
        out.push({
          key: `${p.type_index}-${p.instance}-${i}-${k}`,
          typeIndex: p.type_index,
          instance: p.instance,
          label,
          role: c.role,
          // y_svg = shH - y_mne (contract §2: MNE Y-up → canvas/SVG Y-down)
          svgPts: c.vertices.map(([x, y]) => [x, shH - y]),
          // A tile drawn as one shape: label it for what it is rather than
          // letting it read as a single oversized carton.
          fusedCartons: c.fused && worth > 1 ? worth : 0,
          // Only worth showing a per-half letter when the halves are drawn.
          showRole: cartons.length > 1,
        });
      });
    });
    return out;
  }, [result, shH]);

  if (!result) return null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: P.bg, overflow: 'hidden' }}>
      <DotGrid />
      <div
        ref={wrapperRef}
        onMouseDown={beginPanDrag}
        style={{ position: 'absolute', inset: 0, cursor: 'grab' }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          // No viewBox: 1 SVG user unit = 1 CSS pixel, matching LayoutView so the
          // <g transform> arithmetic lines up with useCanvasControls pan/zoom.
          style={{ display: 'block', position: 'absolute', inset: 0 }}
        >
          <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
            {/* Sheet background (mm) */}
            <rect
              x={0} y={0} width={shW} height={shH}
              fill={P.sheet} stroke={P.rule}
              strokeWidth={1} vectorEffect="non-scaling-stroke"
            />

            {/* Edge margin strips (gripper/tail/guides). Same Y-flip mapping as LayoutView:
                  bottom (MNE y∈[0,bottom])       → SVG y∈[shH-bottom, shH]
                  top    (MNE y∈[shH-top, shH])   → SVG y∈[0, top]
                Drawn behind placements so the artwork reads on top. */}
            {margins && (() => {
              const m = margins;
              const fillC = P.dangerSoft;
              const strokeC = P.dangerLine;
              const strips = [];
              if (m.bottom > 0) strips.push({ key: 'b', x: 0, y: shH - m.bottom, w: shW, h: m.bottom, label: `${fmtLen(m.bottom)} gripper`, lx: shW / 2, ly: shH - m.bottom / 2 });
              if (m.top > 0)    strips.push({ key: 't', x: 0, y: 0, w: shW, h: m.top, label: `${fmtLen(m.top)} tail`, lx: shW / 2, ly: m.top / 2 });
              if (m.left > 0)   strips.push({ key: 'l', x: 0, y: 0, w: m.left, h: shH, label: null });
              if (m.right > 0)  strips.push({ key: 'r', x: shW - m.right, y: 0, w: m.right, h: shH, label: null });
              return strips.map((s) => (
                <g key={s.key}>
                  <rect x={s.x} y={s.y} width={s.w} height={s.h}
                    fill={fillC} stroke={strokeC}
                    strokeWidth={1} strokeDasharray="4,3" vectorEffect="non-scaling-stroke" />
                  {s.label && s.h >= 6 && (
                    <text x={s.lx} y={s.ly} fill={strokeC} fontSize={Math.min(s.h * 0.5, 8)}
                      textAnchor="middle" dominantBaseline="central" style={{ fontFamily: MO }}>{s.label}</text>
                  )}
                </g>
              ));
            })()}

            {/* One polygon per CARTON, not per placement — see `items` above.
                Vertices are final for both cases: rectangular and L-shaped dies
                alike render as a <polygon> with all N points, so a concave
                notch stays empty (contract §3.5).

                The two halves of a tile share their type's colour, because
                they ARE the same carton; the second is drawn a shade lighter
                and both carry a cut-line stroke, so the interlock reads as two
                pieces at a glance instead of one block. */}
            {items.map((it) => {
              const color = TC[it.typeIndex % TC.length];
              const pointsStr = it.svgPts.map((v) => v.join(',')).join(' ');
              // bbox center for the label
              const xs = it.svgPts.map((q) => q[0]);
              const ys = it.svgPts.map((q) => q[1]);
              const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
              const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
              const shade = it.role === 'B' ? 'AB' : 'D9';   // hex alpha
              const suffix = it.showRole ? ` ${it.role || ''}`.trimEnd()
                : (it.fusedCartons ? ` x${it.fusedCartons}` : '');
              return (
                <g key={it.key}>
                  <polygon
                    points={pointsStr}
                    fill={color + shade}
                    stroke={P.sheet}
                    strokeWidth={0.75}
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={cx} y={cy}
                    fill={P.sheet} fontWeight={700} fontSize={7}
                    textAnchor="middle" dominantBaseline="central"
                    style={{ fontFamily: MO, pointerEvents: 'none' }}
                  >
                    {`${String(it.label).slice(0, 8)} #${it.instance}${suffix}`}
                  </text>
                </g>
              );
            })}

            {/* Sheet outline on top */}
            <rect x={0} y={0} width={shW} height={shH}
              fill="none" stroke={P.ink} strokeWidth={1} vectorEffect="non-scaling-stroke" />
          </g>
        </svg>

        {/* Screen-space chrome: rulers / crop marks / colour bar (PR ⑦).
            Sits above the canvas but is pointer-transparent, so panning still
            works anywhere on the sheet. */}
        <SheetChrome ox={pan.x} oy={pan.y} scale={zoom} sheetW={shW} sheetH={shH} />
      </div>

      {/* Fit button */}
      <button
        onClick={() => fit({ contentW: shW, contentH: shH, padding: 0.04, maxZoom: 4 })}
        style={{
          position: 'absolute', right: 10, bottom: 10,
          fontFamily: MO, fontSize: 10, padding: '4px 10px', borderRadius: 4,
          background: P.elev, color: P.ink, border: `0.5px solid ${P.rule}`, cursor: 'pointer',
        }}
      >Fit</button>
    </div>
  );
}
